import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  ListGroup,
  ListGroupItem,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import SectionState from "./SectionState";
import {
  UsuarioFormState,
  RolDto,
  SucursalCatalogo,
  UsuarioSucursalForm,
  EmpresaCatalogo,
} from "../../../types/ajustes";
import classNames from "classnames";

interface UsuarioModalProps {
  isOpen: boolean;
  toggle: () => void;
  formState: UsuarioFormState;
  onChange: (field: keyof UsuarioFormState, value: UsuarioFormState[keyof UsuarioFormState]) => void;
  onSubmit: () => void;
  saving: boolean;
  roles: RolDto[];
  sucursalesDisponibles: SucursalCatalogo[];
  cargandoSucursales: boolean;
  errorSucursales: string | null;
  onRecargarSucursales: () => void;
  empresasDisponibles: EmpresaCatalogo[];
  cargandoEmpresas: boolean;
  errorEmpresas: string | null;
  onRecargarEmpresas: () => void;
  errors?: Partial<Record<keyof UsuarioFormState, string>>;
}

const UsuarioModal = ({
  isOpen,
  toggle,
  formState,
  onChange,
  onSubmit,
  saving,
  roles,
  sucursalesDisponibles,
  cargandoSucursales,
  errorSucursales,
  onRecargarSucursales,
  empresasDisponibles,
  cargandoEmpresas,
  errorEmpresas,
  onRecargarEmpresas,
  errors = {},
}: UsuarioModalProps) => {
  const [activeTab, setActiveTab] = useState<"general" | "empresas" | "roles" | "sucursales">(
    "general"
  );
  const [searchSucursal, setSearchSucursal] = useState("");
  const [fuenteActiva, setFuenteActiva] = useState<string | null>(null);
  const [searchRoles, setSearchRoles] = useState("");
  const [searchEmpresas, setSearchEmpresas] = useState("");

  const formatTexto = (texto: string) => {
    if (!texto) return "";
    return texto.charAt(0).toUpperCase() + texto.slice(1).toLowerCase();
  };

  const handleInput =
    (field: keyof UsuarioFormState) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const target = event.target;
      const value = target.type === "checkbox" ? target.checked : target.value;
      onChange(field, value as never);
    };

  const handleRoleToggle =
    (rolId: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      const seleccionados = new Set(formState.rolesIds);
      if (checked) {
        seleccionados.add(rolId);
      } else {
        seleccionados.delete(rolId);
      }
      onChange("rolesIds", Array.from(seleccionados));
    };

  const rolesFiltrados = useMemo(() => {
    const termino = searchRoles.trim().toLowerCase();
    if (!termino) return roles;
    return roles.filter((rol) => {
      const nombre = rol.nombre.toLowerCase();
      const descripcion = (rol.descripcion ?? "").toLowerCase();
      return nombre.includes(termino) || descripcion.includes(termino);
    });
  }, [roles, searchRoles]);

  const seleccionadasSet = useMemo(
    () =>
      new Set(
        formState.sucursales.map(
          (item) => `${item.fuente}__${item.idsucursal}`
        )
      ),
    [formState.sucursales]
  );

  const empresasSeleccionadas = useMemo(() => {
    return new Set((formState.empresasIds ?? []).map((id) => Number(id)));
  }, [formState.empresasIds]);

  const empresasFiltradas = useMemo(() => {
    const termino = searchEmpresas.trim().toLowerCase();
    if (!termino) {
      return empresasDisponibles;
    }
    return empresasDisponibles.filter((empresa) => {
      const nombre = empresa.nombre?.toLowerCase() ?? "";
      const codigo = empresa.codigo?.toLowerCase() ?? "";
      return nombre.includes(termino) || codigo.includes(termino);
    });
  }, [empresasDisponibles, searchEmpresas]);

  const sucursalesAgrupadas = useMemo(() => {
    const termino = searchSucursal.trim().toLowerCase();
    const mapa = new Map<string, SucursalCatalogo[]>();
    sucursalesDisponibles.forEach((item) => {
      const coincide =
        termino.length === 0 ||
        item.nombre.toLowerCase().includes(termino) ||
        item.fuente.toLowerCase().includes(termino);
      if (!coincide) return;
      const lista = mapa.get(item.fuente) ?? [];
      lista.push(item);
      mapa.set(item.fuente, lista);
    });
    return Array.from(mapa.entries()).map(([fuente, items]) => ({
      fuente,
      items: items.sort((a, b) => a.nombre.localeCompare(b.nombre)),
    }));
  }, [searchSucursal, sucursalesDisponibles]);

  useEffect(() => {
    if (sucursalesAgrupadas.length === 0) {
      if (fuenteActiva !== null) {
        setFuenteActiva(null);
      }
      return;
    }
    const existe = sucursalesAgrupadas.some(
      (grupo) => grupo.fuente === fuenteActiva
    );
    if (!existe) {
      setFuenteActiva(sucursalesAgrupadas[0].fuente);
    }
  }, [fuenteActiva, sucursalesAgrupadas]);

  const gruposVisibles =
    fuenteActiva && sucursalesAgrupadas.some((g) => g.fuente === fuenteActiva)
      ? sucursalesAgrupadas.filter((grupo) => grupo.fuente === fuenteActiva)
      : sucursalesAgrupadas;

  const updateSucursales = (
    updater: (prev: UsuarioSucursalForm[]) => UsuarioSucursalForm[]
  ) => {
    const actuales = formState.sucursales ?? [];
    onChange("sucursales", updater(actuales));
  };

  const toggleSucursal = (registro: SucursalCatalogo, habilitar: boolean) => {
    updateSucursales((prev) => {
      if (habilitar) {
        const exists = prev.some(
          (item) =>
            item.fuente === registro.fuente &&
            item.idsucursal === registro.idsucursal
        );
        if (exists) {
          return prev;
        }
        return [
          ...prev,
          {
            fuente: registro.fuente,
            idsucursal: registro.idsucursal,
            sucursalNombre: registro.nombre,
          },
        ];
      }
      return prev.filter(
        (item) =>
          !(
            item.fuente === registro.fuente &&
            item.idsucursal === registro.idsucursal
          )
      );
    });
  };

  const seleccionarGrupo = (items: SucursalCatalogo[], habilitar: boolean) => {
    updateSucursales((prev) => {
      let lista = prev;
      items.forEach((item) => {
        const existe = lista.some(
          (sel) =>
            sel.fuente === item.fuente && sel.idsucursal === item.idsucursal
        );
        if (habilitar && !existe) {
          lista = [
            ...lista,
            {
              fuente: item.fuente,
              idsucursal: item.idsucursal,
              sucursalNombre: item.nombre,
            },
          ];
        } else if (!habilitar && existe) {
          lista = lista.filter(
            (sel) =>
              !(
                sel.fuente === item.fuente &&
                sel.idsucursal === item.idsucursal
              )
          );
        }
      });
      return lista;
    });
  };

  const toggleEmpresa = (empresaId: number, habilitar: boolean) => {
    const actual = new Set(formState.empresasIds ?? []);
    if (habilitar) {
      actual.add(empresaId);
    } else {
      actual.delete(empresaId);
    }
    onChange("empresasIds", Array.from(actual));
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      modalClassName="usuario-modal-wrapper"
      contentClassName="usuario-modal-content"
    >
      <ModalHeader toggle={toggle}>{formState.nombreUsuario || "Usuario"}</ModalHeader>
      <ModalBody>
        <Nav pills className="arrow-navtabs nav-success mb-3">
          <NavItem>
            <NavLink
              href="#"
              className={classNames({ active: activeTab === "general" })}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab("general");
              }}
            >
              Datos generales
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              href="#"
              className={classNames({ active: activeTab === "empresas" })}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab("empresas");
              }}
            >
              Empresas
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              href="#"
              className={classNames({ active: activeTab === "roles" })}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab("roles");
              }}
            >
              Roles
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              href="#"
              className={classNames({ active: activeTab === "sucursales" })}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab("sucursales");
              }}
            >
              Sucursales
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={activeTab}>
          <TabPane tabId="general">
            <Form>
              <FormGroup>
                <Label>Nombre de usuario</Label>
                <Input
                  value={formState.nombreUsuario}
                  onChange={handleInput("nombreUsuario")}
                  invalid={Boolean(errors.nombreUsuario)}
                />
                {errors.nombreUsuario && (
                  <FormFeedback>{errors.nombreUsuario}</FormFeedback>
                )}
              </FormGroup>
              <FormGroup>
                <Label>Contraseña</Label>
                <Input
                  type="password"
                  value={formState.contrasena}
                  onChange={handleInput("contrasena")}
                  invalid={Boolean(errors.contrasena)}
                />
                {errors.contrasena && (
                  <FormFeedback>{errors.contrasena}</FormFeedback>
                )}
              </FormGroup>
              <FormGroup>
                <Label>Correo corporativo</Label>
                <Input
                  type="email"
                  value={formState.correoCorporativo}
                  onChange={handleInput("correoCorporativo")}
                  invalid={Boolean(errors.correoCorporativo)}
                />
                {errors.correoCorporativo && (
                  <FormFeedback>{errors.correoCorporativo}</FormFeedback>
                )}
              </FormGroup>
              <FormGroup>
                <Label>Número de cédula</Label>
                <Input
                  value={formState.numeroCi}
                  onChange={handleInput("numeroCi")}
                  invalid={Boolean(errors.numeroCi)}
                  placeholder="Ej. 1234567-8"
                />
                {errors.numeroCi && <FormFeedback>{errors.numeroCi}</FormFeedback>}
              </FormGroup>
              <FormGroup check className="mb-3">
                <Input
                  type="checkbox"
                  checked={formState.requiereDobleFactor}
                  onChange={handleInput("requiereDobleFactor")}
                />
                <Label check className="ms-2">
                  Requiere doble factor
                </Label>
              </FormGroup>
            </Form>
          </TabPane>
          <TabPane tabId="empresas">
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Input
                type="search"
                placeholder="Buscar empresa"
                value={searchEmpresas}
                onChange={(event) => setSearchEmpresas(event.target.value)}
                className="flex-fill"
              />
              <Button color="link" onClick={onRecargarEmpresas}>
                Recargar empresas
              </Button>
            </div>
            <SectionState
              loading={cargandoEmpresas}
              error={errorEmpresas}
              description="Cargando empresas..."
              onRetry={onRecargarEmpresas}
            />
            {!cargandoEmpresas && !errorEmpresas && empresasDisponibles.length === 0 && (
              <p className="text-muted mb-0">No hay empresas registradas.</p>
            )}
            {!cargandoEmpresas && !errorEmpresas && empresasDisponibles.length > 0 && (
              <div className="row">
                {empresasFiltradas.map((empresa) => {
                  const label = empresa.nombre ?? empresa.codigo ?? `Empresa ${empresa.id}`;
                  return (
                    <div className="col-12 col-md-6 mb-2" key={empresa.id}>
                      <div className="form-check">
                        <Input
                          id={`empresa-${empresa.id}`}
                          type="checkbox"
                          checked={empresasSeleccionadas.has(empresa.id)}
                          onChange={(event) =>
                            toggleEmpresa(empresa.id, event.target.checked)
                          }
                        />
                        <Label className="form-check-label" htmlFor={`empresa-${empresa.id}`}>
                          {label}
                        </Label>
                      </div>
                    </div>
                  );
                })}
                {empresasFiltradas.length === 0 && (
                  <div className="col-12">
                    <p className="text-muted mb-0">Ninguna empresa coincide con el filtro.</p>
                  </div>
                )}
              </div>
            )}
          </TabPane>
          <TabPane tabId="roles">
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Input
                type="search"
                placeholder="Buscar rol"
                value={searchRoles}
                onChange={(event) => setSearchRoles(event.target.value)}
                className="flex-fill"
              />
            </div>
            <FormGroup>
              <Label>Roles disponibles</Label>
              {rolesFiltrados.length === 0 ? (
                <p className="text-muted mb-0">No hay roles disponibles.</p>
              ) : (
                <ListGroup>
                  {rolesFiltrados.map((rol) => {
                    const seleccionado = formState.rolesIds.includes(rol.id);
                    return (
                      <ListGroupItem tag="label" key={rol.id} className="d-flex align-items-center">
                        <Input
                          className="form-check-input me-2"
                          type="checkbox"
                          checked={seleccionado}
                          onChange={handleRoleToggle(rol.id)}
                        />
                        {rol.nombre}
                      </ListGroupItem>
                    );
                  })}
                </ListGroup>
              )}
            </FormGroup>
          </TabPane>
          <TabPane tabId="sucursales">
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Input
                type="search"
                placeholder="Buscar sucursal"
                value={searchSucursal}
                onChange={(event) => setSearchSucursal(event.target.value)}
                className="flex-fill"
              />
              <Button color="link" onClick={onRecargarSucursales}>
                Recargar sucursales
              </Button>
            </div>
            <SectionState
              loading={cargandoSucursales}
              error={errorSucursales}
              description="Cargando sucursales..."
              onRetry={onRecargarSucursales}
            />
            {!cargandoSucursales && !errorSucursales && sucursalesAgrupadas.length === 0 && (
              <p className="text-muted">No hay sucursales disponibles.</p>
            )}
            {!cargandoSucursales &&
              !errorSucursales &&
              sucursalesAgrupadas.length > 1 && (
                <Nav pills className="arrow-navtabs nav-success mb-3">
                  {sucursalesAgrupadas.map((grupo) => (
                    <NavItem key={grupo.fuente}>
                      <NavLink
                        href="#"
                        className={classNames({ active: fuenteActiva === grupo.fuente })}
                        onClick={(event) => {
                          event.preventDefault();
                          setFuenteActiva(grupo.fuente);
                        }}
                      >
                        {formatTexto(grupo.fuente)}
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>
              )}
            {!cargandoSucursales &&
              !errorSucursales &&
              gruposVisibles.map((grupo) => (
                <div className="rounded border p-3 mb-3" key={grupo.fuente}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>{formatTexto(grupo.fuente)}</strong>
                    <div className="btn-group">
                      <Button
                        type="button"
                        color="link"
                        size="sm"
                        onClick={() => seleccionarGrupo(grupo.items, true)}
                      >
                        Seleccionar todo
                      </Button>
                      <Button
                        type="button"
                        color="link"
                        size="sm"
                        onClick={() => seleccionarGrupo(grupo.items, false)}
                      >
                        Deseleccionar
                      </Button>
                    </div>
                  </div>
                  <div className="row">
                    {grupo.items.map((item) => {
                      const clave = `${item.fuente}__${item.idsucursal}`;
                      return (
                        <div className="col-12 col-md-4" key={clave}>
                          <div className="form-check">
                            <Input
                              id={clave}
                              type="checkbox"
                              checked={seleccionadasSet.has(clave)}
                              onChange={(event) =>
                                toggleSucursal(item, event.target.checked)
                              }
                            />
                            <Label className="form-check-label" htmlFor={clave}>
                              {formatTexto(item.nombre)}
                            </Label>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
          </TabPane>
        </TabContent>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between">
        <Button color="light" onClick={toggle} disabled={saving}>
          Cancelar
        </Button>
        <Button color="primary" onClick={onSubmit} disabled={saving}>
          {saving ? "Guardando..." : "Guardar"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default UsuarioModal;
